import {
  type DtoCreateExpenseReq,
  type DtoExpense,
  type DtoGetBorrowerExpensesResp,
  type DtoUpdateExpenseReq,
  type DomainExpenseType,
} from "@porchpass/api-client";
import {
  commaSeparatedNumbersToArray,
  createIsoDate,
  parseIsoDate,
  zeroDate,
} from "src/utils";
import { requiredField } from "./required";

export type ExpenseFormData = {
  id?: string;
  expense_type?: DomainExpenseType;
  expense_type_other?: string;
  amount?: string;
  children_ages?: string;
  from_month?: string;
  from_year?: string;
  /** End date as saved on the server; empty when the expense never had one. */
  end_date?: string;
  to_month?: string;
  to_year?: string;
};

export type ExpenseListFormData = {
  expenses: Partial<ExpenseFormData>[];
};

function buildExpenseReq(
  data: ExpenseFormData,
): DtoCreateExpenseReq | DtoUpdateExpenseReq {
  const mappedData: DtoCreateExpenseReq = {
    amount: parseInt(requiredField(data.amount, "amount")),
    expense_type: requiredField(data.expense_type, "expense_type"),
    children_ages: commaSeparatedNumbersToArray(data.children_ages),
  };

  if (data.to_year && data.to_month) {
    mappedData.end_date = createIsoDate(
      parseInt(data.to_year),
      parseInt(data.to_month),
    );
  }

  if (data.from_year && data.from_month) {
    mappedData.start_date = createIsoDate(
      parseInt(data.from_year),
      parseInt(data.from_month),
    );
  }

  if (data.expense_type === "OTHER_SUPPORT") {
    mappedData.expense_type_other = data.expense_type_other;
  }

  return mappedData;
}

export function toCreateExpenseReq(data: ExpenseFormData): DtoCreateExpenseReq {
  return buildExpenseReq(data);
}

export function toUpdateExpenseReq(data: ExpenseFormData): DtoUpdateExpenseReq {
  const mappedData = buildExpenseReq(data);

  // The API only changes end_date when it is sent, so an expense that was made
  // ongoing again has to send the zero date to clear the saved end date.
  if (!mappedData.end_date && data.end_date && data.end_date !== zeroDate) {
    mappedData.end_date = zeroDate;
  }

  return mappedData;
}

export function fromExpensesList(
  data: DtoGetBorrowerExpensesResp,
): ExpenseListFormData {
  const expenses = (data.expenses ?? []).map((expense) =>
    mapExpenseToForm(expense),
  );

  return { expenses };
}

function mapExpenseToForm(expense: DtoExpense): Partial<ExpenseFormData> {
  const mappedData: Partial<ExpenseFormData> = {
    ...expense,
    amount: String(expense.amount),
    expense_type_other: expense?.expense_type_other,
    children_ages: expense?.children_ages?.join(", "),
  };

  if (expense.start_date) {
    const { year: from_year, month: from_month } = parseIsoDate(
      expense.start_date,
    );
    mappedData.from_year = from_year;
    mappedData.from_month = from_month;
  }

  if (expense.end_date) {
    const { year: to_year, month: to_month } = parseIsoDate(expense.end_date);
    mappedData.to_year = to_year;
    mappedData.to_month = to_month;
  }

  return mappedData;
}
